import React, { useCallback, useMemo, useRef } from 'react';

import { Button, Modal, Typography } from 'antd';

import { AlertGlobalProvider } from './global';

export type AlertSize = 'sm' | 'md' | 'xs';

interface IProps {
  opened: boolean;
  message: React.ReactNode;
  title?: React.ReactNode;
  caption?: React.ReactNode;
  buttonOk?: React.ReactNode;
  buttonOkDanger?: boolean;
  buttonCancel?: React.ReactNode;
  confirmation?: boolean;
  global?: boolean;
  onClose: (ok: boolean) => void;
  noPadding?: boolean;
  showTitle?: boolean;
  size?: AlertSize;
}

export interface IAlertShowParams {
  message?: React.ReactNode | string;
  title?: React.ReactNode;
  caption?: React.ReactNode;
  confirmation?: boolean;
  size?: AlertSize;
  buttonOk?: React.ReactNode;
  buttonOkDanger?: boolean;
  buttonCancel?: React.ReactNode;
}

interface FrozenContent {
  message?: React.ReactNode;
  title?: React.ReactNode;
  confirmation?: boolean;
  buttonOk?: React.ReactNode;
  buttonOkDanger?: boolean;
  buttonCancel?: React.ReactNode;
}

const SIZE_MAP: Record<AlertSize, number> = {
  xs: 400,
  sm: 600,
  md: 900
};

type AlertComponent = React.FC<IProps> & {
  Global: typeof AlertGlobalProvider;
  show(params: string): Promise<boolean>;
  show(params: IAlertShowParams): Promise<boolean>;
  confirm(params: string): Promise<boolean>;
  confirm(params: IAlertShowParams): Promise<boolean>;
};

const AlertInner: React.FC<IProps> = props => {
  const { opened, caption, noPadding, showTitle = true, size, onClose } = props;

  const lastContentRef = useRef<FrozenContent>({});

  if (opened) {
    lastContentRef.current = {
      message: props.message,
      title: props.title,
      confirmation: props.confirmation,
      buttonOk: props.buttonOk,
      buttonOkDanger: props.buttonOkDanger,
      buttonCancel: props.buttonCancel
    };
  }

  const { message, title, confirmation, buttonOk, buttonCancel, buttonOkDanger } = lastContentRef.current;

  const handleOk = useCallback(() => onClose(true), [onClose]);
  const handleCancel = useCallback(() => onClose(false), [onClose]);

  const modalWidth = SIZE_MAP[size ?? 'sm'];

  const modalTitle = useMemo(() => {
    if (!showTitle) return null;

    return (
      <div id='alert-dialog-title'>
        <Typography className='flex items-center justify-between font-bold!'>
          {title || (confirmation ? 'Confirmação' : 'Atenção')}
        </Typography>
        {caption && (
          <Typography.Text className='flex items-center justify-between text-xs font-bold text-gray-500'>
            {caption}
          </Typography.Text>
        )}
      </div>
    );
  }, [showTitle, title, confirmation, caption]);

  const footer = useMemo(
    () => (
      <div className='flex justify-end gap-2'>
        {confirmation && (
          <Button id='alert-cancel-button' type='text' onClick={handleCancel}>
            {buttonCancel || 'Cancelar'}
          </Button>
        )}
        <Button id='alert-confirm-button' type='primary' danger={buttonOkDanger ?? false} onClick={handleOk}>
          {buttonOk || 'Confirmar'}
        </Button>
      </div>
    ),
    [confirmation, handleCancel, handleOk, buttonCancel, buttonOk, buttonOkDanger]
  );

  return (
    <Modal
      open={opened}
      width={modalWidth}
      maskClosable={false}
      onCancel={handleCancel}
      title={modalTitle}
      footer={footer}
      destroyOnHidden
    >
      <div id='alert-dialog-content' className={noPadding ? 'p-0' : undefined}>
        {typeof message === 'string' ? <Typography.Text>{message}</Typography.Text> : message}
      </div>
    </Modal>
  );
};

const Alert = AlertInner as AlertComponent;

Alert.show = (params: string | IAlertShowParams) => {
  const paramsData = typeof params === 'string' ? { message: params } : params;
  return AlertGlobalProvider.show({
    ...paramsData,
    size: paramsData.size || 'sm',
    buttonOkDanger: paramsData.buttonOkDanger ?? false
  });
};

Alert.confirm = (params: string | IAlertShowParams) => {
  const paramsData = typeof params === 'string' ? { message: params } : params;
  return AlertGlobalProvider.show({
    ...paramsData,
    confirmation: true,
    size: paramsData.size || 'sm',
    buttonOkDanger: paramsData.buttonOkDanger ?? false
  });
};

Alert.Global = AlertGlobalProvider;

export default Alert;
